import type { CSSProperties } from 'react'
import type { TableProps } from 'antd'
import React, { useState, useEffect, useRef } from 'react'
import { Pagination, Empty } from 'antd'
import { get, isEqual, pickBy } from 'lodash'
import 'src/styles/components/common/easyCustomTable.less'

type Payload = {
  PageIndex: number,
  Limit: number,
  [key: string]: any
}

export type RefIns = {
  reload: (params?: Partial<Payload>) => Promise<void>
}

export type Props = {
  onRef?: (ins: RefIns) => void,
  extraPayload?: { [key: string]: any },
  remoteMethod?: (payload: Payload) => Promise<any>,
  // 后端返回数据结构取值字段
  remoteField?: {
    total?: string,
    data?: string,
  },
  // 布局配置
  rowField?: {
    itemCount?: number, // 一行放几个 item
    gapHorizontal?: number, // item 横向间距
    gapVertical?: number, // item 纵向间距
  },
  rowItem?: (record: any, index: number) => React.ReactElement,
  rowKey: string,
  pagination?: TableProps<any>['pagination'],
  className?: string,
  style?: CSSProperties
}

const EasyCustomTable = (props: Props) => {
  const payload = useRef<Payload>({
    PageIndex: 1,
    Limit: 10
  })
  const updatePayload = (data: Partial<Payload>) => {
    Object.assign(payload.current, data)
  }
  useEffect(() => { updatePayload({ PageIndex: 1, ...props.extraPayload }) }, [props.extraPayload])

  const [data, setData] = useState<{ total: number, data: any[] }>({ total: 0, data: [] })
  const getData = () => {
    return props.remoteMethod?.(payload.current).then(res => {
      if (res) {
        const { data = 'Data', total = 'TotalCount' } = props.remoteField ?? {}
        const totalCount = get(res, total)
        const dataSource = get(res, data)

        setData(prev => {
          const newData = { total: totalCount, data: dataSource }
          return isEqual(prev, newData) ? prev : newData
        })
      }
    })
  }
  useEffect(() => { getData() }, [props.extraPayload])

  useEffect(() => {
    props.onRef?.({
      reload: params => {
        updatePayload(pickBy(params, v => v !== undefined))
        return getData() ?? Promise.resolve()
      }
    })
  }, [])

  const tableDom = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (data.data.length && tableDom.current) {
      const offsetHeight = tableDom.current.offsetHeight
      const scrollHeight = tableDom.current.scrollHeight
      if (scrollHeight > offsetHeight) {
        tableDom.current.style.paddingRight = '8px'
      }
    }
  }, [data.data.length])

  const pageOption = {
    size: 'small' as 'small',
    showSizeChanger: true,
    pageSizeOptions: ['5', '10', '20'],
    showQuickJumper: true,
    showTotal: (total: number) => `共 ${total} 条记录`,
    ...props.pagination,
    total: data.total,
    current: payload.current.PageIndex,
    pageSize: payload.current.Limit,
    onChange: (current: number, pageSize: number) => {
      updatePayload({ PageIndex: current, Limit: pageSize })
      getData()
    }
  }

  const { itemCount = 4, gapHorizontal = 12, gapVertical = 12 } = props.rowField ?? {}
  return (
    <div className={`easy-customtable ${props.className}`} style={props.style}>
      <div ref={tableDom} className='easy-customtable__table' style={{ maxHeight: 'calc(100% - 38px)' }}>
        {
          data.data.map((item, index) => {
            const width = `calc(${100 / itemCount}% - ${(itemCount - 1) * gapHorizontal / itemCount}px)`
            const style: CSSProperties = {
              width,
              maxHeight: `calc(100% - ${props.pagination === false ? 0 : 38}px)`,
              marginRight: (index + 1) % itemCount === 0 ? 0 : gapHorizontal,
              marginBottom: gapVertical,
            }
            return (
              <div key={item[props.rowKey]} className='easy-customtable__table-item' style={style}>
                {props.rowItem?.(item, index)}
              </div>
            )
          })
        }
        {!data.data.length ? <Empty /> : null}
      </div>
      {
        props.pagination !== false
          ? (
            <div className='easy-customtable__pagination'>
              <Pagination {...pageOption} />
            </div>
          )
          : null
      }
    </div>
  )
}

EasyCustomTable.defaultProps = {
  rowKey: 'ID'
}

export default EasyCustomTable